import { Body2, Button, UIHeading4 } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { type FC } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { Ticker } from 'shared/components/Ticker';
import { toCssImageUrl } from 'shared/utils/toCssImageUrl';
import { type TSpecialProjectBannerContext } from 'shared/types/bannerContext';

import styles from './SpecialProjectMobile.css';

export type TSpecialProjectMobileProps = {
  context: TSpecialProjectBannerContext;
  height: number;
};

/**
 * Mobile-версия баннера спецпроекта.
 *
 * Перенесена по аналогии с mobile-search-frontend:
 * src/Serp/shared/components/AdfoxBanner/internal/components/NewbuildingSpecialProject/SpecialProjectBanner.tsx
 * (бегущая строка `Ticker` из @cian/adfox-templates заменена на локальный `Ticker`).
 */
export const SpecialProjectMobile: FC<TSpecialProjectMobileProps> = ({ context, height }) => {
  const {
    bgImage,
    title,
    text,
    url,
    buttonText,
    substrateNotNeeded,
    substrateLogoNotNeeded,
    logo,
    label,
    disclaimer,
    mobImage,
    mortgageWarning,
    mortgageTermsWarning,
  } = context;

  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      data-testid="special-project-mobile"
      href={url}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className={clsx(
        styles['container'],
        !Number(substrateNotNeeded) && styles['container--without-substrate'],
        !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
        withMortgageAnnotations && styles['container--mortgage-annotations'],
      )}
      style={{ backgroundImage: toCssImageUrl(mobImage || bgImage), height }}
    >
      <div className={styles['content']}>
        <div className={styles['content-wrapper']}>
          <div className={styles['heading']}>
            <div className={styles['heading-content']}>
              {!!label && (
                <div className={styles['label-wrapper']}>
                  <AdLabel label={label} />
                </div>
              )}
              <UIHeading4 color="current_color">{title}</UIHeading4>
            </div>
            <div className={styles['logo']}>
              <img width={48} height={48} className={styles['logo-image']} src={logo} alt={title} />
            </div>
          </div>
          <Body2 color="current_color">{text}</Body2>
          <div className={styles['button-wrapper']}>
            <Button fullWidth theme="fill_white_primary" size="M">
              {buttonText}
            </Button>
          </div>
        </div>
        {!!disclaimer && (
          <div className={styles['disclaimer']}>
            <Ticker theme="light" text={disclaimer} />
          </div>
        )}
        {withMortgageAnnotations && (
          <div className={styles['mortgage-disclaimer']}>
            <MortgageAnnotations
              height={height}
              mortgageWarning={mortgageWarning}
              mortgageTermsWarning={mortgageTermsWarning}
            />
          </div>
        )}
      </div>
    </a>
  );
};
